import {call, race} from 'redux-saga/effects'
import callEndpoint from './callEndpoint'
import createApiError from '../utils/createApiError'
import refreshTokens from '$src/api/endpoints/auth/refresh'
import {getMstStore} from '$src/bootstrap/rootSaga'
import {IStore} from '$src/bootstrap/Store'

interface RequestOptions {
  retryOn401?: boolean
  numberOfRetries?: number
  retryTimeout?: number // in ms
  withTokens?: boolean
}

const defaultRequestOptions: RequestOptions = {
  retryOn401: true,
  numberOfRetries: 0,
  retryTimeout: 20000,
  withTokens: true,
}

const isResponse401 = (response: any) => {
  return response.status === 401
}

const rejectWithApiError = (response: any) =>
  response.text().then(
    (text: any) => {
      let errorMessage = ''
      let json: any = {}
      try {
        json = JSON.parse(text)
        errorMessage = json.message || json.error
      } catch (e) {
        errorMessage = text
      }

      return Promise.reject(createApiError(errorMessage, response))
    },
    () => {
      return Promise.reject(createApiError('Unkown Api error', response))
    },
  )

function delay<T>(dur: number, val: T): Promise<T> {
  return new Promise(resolve => setTimeout(() => resolve(val), dur))
}

/**
 * Sends a request to the server. This is not meant to be used directly. Use `callEndpoint()` instead.
 * The reason we don't call this directly is explained in $src/api/endpoints/Readme.md
 */
export default function* request(endpoint: string, originalOptions: RequestOptions): Generator {
  const url = process.env.REACT_APP_BACKEND_BASE_URL + endpoint

  const options: any = {
    ...defaultRequestOptions,
    ...originalOptions,
  }

  const mstStore: IStore = yield getMstStore()

  const authState = mstStore.auth.getState()

  if (options.withTokens === true && !authState.isLoggedIn) {
    new Error(`Endpoint '${endpoint}' requires Dispatcher to already have the auth tokens, but it doesn't.`)
  }

  options.headers = {
    'content-type': 'application/json',
    ...options.headers,
  }

  const tokensAddedToHeader = authState.isLoggedIn
  if (authState.isLoggedIn) {
    options.headers['Authorization'] = `Bearer ${authState.jwt}`
  }

  const doFetch = function*(): Generator {
    const response = yield call(fetch, url, options)
    const responseFn: any = response.status === 204 ? () => {} : [response, response.json]
    if (response.ok) {
      return yield call(responseFn)
    } else if (isResponse401(response) && tokensAddedToHeader && options.retryOn401) {
      try {
        yield callEndpoint(refreshTokens, authState)
        yield call(delay, 10, null)
        return yield call(request, endpoint, {...options, retryOn401: false})
      } catch (e) {
        return yield call(rejectWithApiError, response)
      }
    } else {
      if (isResponse401(response))
        mstStore.auth.setAsLoggedOut()

      return yield call(rejectWithApiError, response)
    }
  }

  if (options.numberOfRetries > 0) {
    const {response, timeout} = yield race({
      response: call(doFetch),
      timeout: call(delay, options.retryTimeout, true),
    })

    if (timeout) {
      return yield call(request, endpoint, {...originalOptions, numberOfRetries: options.numberOfRetries - 1})
    } else {
      return response
    }
  } else {
    return yield call(doFetch)
  }
}



// WEBPACK FOOTER //
// ./src/api/sagas/request.tsx